"use client";

import { MapPin, Stethoscope } from "lucide-react";

import {
  FIELD_CLASSNAME,
  type OnlineSchedulingLocation,
  type OnlineSchedulingProvider,
} from "@/src/components/online-scheduling/types";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/shadcn/ui/select";
import { ExtraSmall } from "@/src/components/typography";
import { cn } from "@/src/lib/shadcn-utils";

import type { ReactNode } from "react";

const ANY_PROVIDER_VALUE = "any";

export function ProviderSelectionField({
  providers,
  selectedProviderId,
  onChange,
  allowAny = false,
  readOnly = false,
}: {
  providers: OnlineSchedulingProvider[];
  selectedProviderId: string | null | undefined;
  onChange: (providerId: string | null) => void;
  allowAny?: boolean;
  readOnly?: boolean;
}) {
  const selectedLabel =
    selectedProviderId === null
      ? "Any available provider"
      : providers.find((provider) => provider.practitionerId === selectedProviderId)?.displayName;

  return (
    <SelectionField icon={<Stethoscope className="h-4 w-4" />} label="Provider">
      {readOnly ? (
        <p className="truncate text-sm font-medium text-foreground">{selectedLabel}</p>
      ) : (
        <Select
          value={
            selectedProviderId === undefined
              ? undefined
              : selectedProviderId === null
                ? ANY_PROVIDER_VALUE
                : selectedProviderId
          }
          onValueChange={(value) => onChange(value === ANY_PROVIDER_VALUE ? null : value)}
        >
          <SelectionTrigger placeholder="Choose a provider" />
          <SelectContent>
            {allowAny && <SelectItem value={ANY_PROVIDER_VALUE}>Any available provider</SelectItem>}
            {providers.map((provider) => (
              <SelectItem key={provider.practitionerId} value={provider.practitionerId}>
                {provider.displayName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </SelectionField>
  );
}

export function LocationSelectionField({
  locations,
  selected,
  onChange,
  readOnly = false,
}: {
  locations: OnlineSchedulingLocation[];
  selected: OnlineSchedulingLocation | null;
  onChange: (location: OnlineSchedulingLocation) => void;
  readOnly?: boolean;
}) {
  return (
    <SelectionField icon={<MapPin className="h-4 w-4" />} label="Location">
      {readOnly || (locations.length === 1 && selected) ? (
        <p className="truncate text-sm font-medium text-foreground">{selected?.displayName ?? "Choose a location"}</p>
      ) : (
        <Select
          value={selected?.locationId}
          onValueChange={(value) => {
            const next = locations.find((location) => location.locationId === value);

            if (next) {
              onChange(next);
            }
          }}
        >
          <SelectionTrigger placeholder="Choose a location" />
          <SelectContent>
            {locations.map((location) => (
              <SelectItem key={location.locationId} value={location.locationId}>
                {location.displayName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </SelectionField>
  );
}

function SelectionField({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <div className={cn("flex items-center gap-2 rounded-xl border px-4 py-4", FIELD_CLASSNAME)}>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent text-foreground">
        {icon}
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <ExtraSmall className="uppercase tracking-[0.12em] text-muted-foreground">{label}</ExtraSmall>
        {children}
      </div>
    </div>
  );
}

function SelectionTrigger({ placeholder }: { placeholder: string }) {
  return (
    <SelectTrigger className="h-auto border-0 bg-transparent px-0 py-0 text-sm font-medium shadow-none focus:ring-0 disabled:opacity-100 [&>span]:truncate">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
  );
}
